import React, { useContext, useEffect, useRef, useState } from "react";
import { calculateTaxAndTaxableValue } from "../../../Helper/calculations";
import { urlToBase64 } from "../../../Helper/utility";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import styles from "./TallyTheme.module.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const TallyTheme = ({ selectedPalette, invoiceDataMaster, themeData }) => {
    const asCtx = useContext(ActiveStoreContext)
    const printRef = useRef();
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const { meta } = themeData;
    const {
        clientData,
        productData,
        totalAmount,
        tncData,
        customFields,
        invoiceDate,
        dueDate,
        invoiceConfig,
    } = invoiceDataMaster;

    const fallbackItemColumns = [
        { key: 'productTitle', label: 'Title', order : 1 },
        { key: 'qty', label: 'Quantity', order: 2 },
        { key: 'price', label: 'Price', order: 3 },
        { key: 'discount', label: 'Discount', order: 4 },
        { key: 'total', label: 'Total Amount', order: 5 },
    ];

    const itemColumns = invoiceConfig?.itemColumns?.length > 0
        ? [...invoiceConfig?.itemColumns]?.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
        : fallbackItemColumns;



    const getColumnValue = (product, key) => {
        switch (key) {
            case 'productTitle':
                return product?.productTitle || product?.serviceName || '';
            case 'price':
                return `₹${( parseFloat(product?.price) || 0).toFixed(2)}`;
            case 'qty':
                return product?.qty || 0;
            case 'hsnCode':
                return product?.hsnCode || '';
            case 'gst':
                return product?.gst || '';
            case 'gstValue':
                return product?.gstValue || '';
            case 'discount':
                return product?.discount || 0;
            case 'total':
                return `₹${( parseFloat(product?.total) || 0).toFixed(2)}`;
            default:
                return '';
        }
    };

    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [base64Logo, setBase64Logo] = useState(null);

    ///// final calculations.. 
    const calculateTotalTaxable = () => {
        return invoiceDataMaster?.productData?.reduce((acc, product) => {
            const price = Number(product?.price) || 0;
            const qty = Number(product?.qty) || 0;
            const discount = Number(product?.discount) || 0;
            const discountType = invoiceDataMaster?.discountType || 'value';

            let finalDiscount = 0;

            if (discountType === "percentage") {
                finalDiscount = (price * discount) / 100;
            } else if (discountType === "value") {
                finalDiscount = discount;
            }

            const priceAfterDiscount = price - finalDiscount;
            const taxablePrice = priceAfterDiscount * qty;

            return acc + Math.max(0, taxablePrice);
        }, 0);
    };

    const calculateTotalTax = () => {
        if (!invoiceDataMaster?.productData?.length) return 0;

        return invoiceDataMaster.productData.reduce((acc, product) => {
            const price = product?.price || 0;
            const qty = product?.qty || 0;
            const discount = product?.discount || 0;
            const discountType = invoiceDataMaster?.discountType || 'value';
            const gstRate = product?.gst || 0;

            let discountedPrice = price;
            if (discountType === 'percentage') {
                discountedPrice = price - ((price * discount) / 100);
            } else {
                discountedPrice = price - discount;
            }

            discountedPrice = Math.max(0, discountedPrice);
            const taxablePrice = discountedPrice * qty;
            const tax = (parseFloat(gstRate) / 100) * taxablePrice;

            return acc + tax;
        }, 0);
    };

    const calculateGrandTotal = () => {
        let grandTotal = calculateTotalTaxable() + calculateTotalTax();

        invoiceDataMaster?.customFields?.forEach((field) => {
            const fieldValue =
                field.calculationType === "percentage"
                    ? (grandTotal * (field.value || 0)) / 100
                    : field.value || 0;
            grandTotal += field.calculationLogic === "add" ? fieldValue : -fieldValue;
        });

        if (invoiceDataMaster?.isRoundingOff) {
            grandTotal = Math.round(grandTotal);
        }

        return grandTotal;
    };

    const fetchBase64Logo = async () => {
        try {
            const base64 = await urlToBase64(activeStore?.invoiceLogo);
            setBase64Logo(base64);
        } catch (error) {
            console.error("Error converting image to base64:", error);
        }
    };

    useEffect(() => {
        fetchBase64Logo();
    }, []);

    // ENTERPRISE PDF GENERATION WITH PROPER A4 FORMATTING
    const handleGeneratePDF = async () => {
        setIsGeneratingPDF(true);

        try {
            const element = printRef.current;

            // Get all computed styles
            const computedStyle = window.getComputedStyle(element);

            // Create a container for rendering
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-99999px';
            container.style.top = '0';
            container.style.width = '210mm'; // A4 width
            // container.style.minHeight = '297mm'; // A4 height
            container.style.padding = '7.5mm';
            container.style.boxSizing = 'border-box';
            container.style.backgroundColor = selectedPalette?.backgroundColor || '#ffffff';
            container.style.color = selectedPalette?.textColor || '#000000';
            container.style.fontFamily = computedStyle.fontFamily;
            container.style.fontSize = computedStyle.fontSize;

            // Clone the content
            const clone = element.cloneNode(true);

            // Apply inline styles to ensure they're captured
            const applyInlineStyles = (sourceEl, targetEl) => {
                const sourceStyle = window.getComputedStyle(sourceEl);
                targetEl.style.cssText = sourceStyle.cssText;

                const sourceChildren = sourceEl.children;
                const targetChildren = targetEl.children;

                for (let i = 0; i < sourceChildren.length; i++) {
                    if (targetChildren[i]) {
                        applyInlineStyles(sourceChildren[i], targetChildren[i]);
                    }
                }
            };

            applyInlineStyles(element, clone);
            container.appendChild(clone);
            document.body.appendChild(container);

            // Wait for fonts and images to load
            await document.fonts.ready;
            await new Promise(resolve => setTimeout(resolve, 500));

            // Generate canvas with high quality
            const canvas = await html2canvas(container, {
                scale: 3, // Higher quality
                useCORS: true,
                logging: false,
                backgroundColor: selectedPalette?.backgroundColor || '#ffffff',
                // width: container.offsetWidth,
                // height: container.offsetHeight,
                // windowWidth: container.offsetWidth,
                // windowHeight: container.offsetHeight,
                imageTimeout: 0,
                removeContainer: false
            });

            // Remove the container
            document.body.removeChild(container);

            // A4 dimensions in mm
            const pdfWidth = 210;
            const pdfHeight = 297;

            // Calculate image dimensions to fit A4
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;

            // Initialize jsPDF
            const pdf = new jsPDF({
                orientation: imgHeight > pdfHeight ? 'portrait' : 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.95);

            // Check if content fits on one page
            if (imgHeight <= pdfHeight) {
                // Single page
                pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
            } else {
                // Multiple pages needed
                let heightLeft = imgHeight;
                let position = 0;
                let page = 0;

                while (heightLeft > 0) {
                    if (page > 0) {
                        pdf.addPage();
                    }

                    const pageCanvas = document.createElement('canvas');
                    const pageContext = pageCanvas.getContext('2d');

                    pageCanvas.width = canvas.width;
                    pageCanvas.height = Math.min(
                        canvas.height - (page * canvas.width * pdfHeight / pdfWidth),
                        canvas.width * pdfHeight / pdfWidth
                    );

                    pageContext.fillStyle = selectedPalette?.backgroundColor || '#ffffff';
                    pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

                    pageContext.drawImage(
                        canvas,
                        0,
                        page * canvas.width * pdfHeight / pdfWidth,
                        canvas.width,
                        pageCanvas.height,
                        0,
                        0,
                        canvas.width,
                        pageCanvas.height
                    );

                    const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
                    // Calculate real slice height in mm (maintaining aspect ratio)
                    const sliceHeightMM = (pageCanvas.height * pdfWidth) / pageCanvas.width;

                    pdf.addImage(pageImgData, 'JPEG', 0, 0, imgWidth, sliceHeightMM);


                    heightLeft -= pdfHeight;
                    page++;
                }
            }

            // Save the PDF
            const fileName = `Invoice_${invoiceDataMaster?.invoiceNumber || 'Document'}_${new Date().getTime()}.pdf`;
            pdf.save(fileName);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    // PROPER BROWSER PRINT - ONLY INVOICE CONTENT
    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const printContent = printRef.current.innerHTML;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Invoice ${invoiceDataMaster?.invoiceNumber || ''}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet">
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    
                    @page {
                        size: A4;
                        margin: 7.5mm;
                    }
                    
                    body {
                        font-family: 'Montserrat', Arial, sans-serif;
                        font-size: 12pt;
                        line-height: 1.4;
                        color: ${selectedPalette?.textColor || '#000000'};
                        background-color: ${selectedPalette?.backgroundColor || '#ffffff'};
                        padding: 10mm;
                    }
                    
                    .${styles.tallyWrapper} {
                        width: 100%;
                        max-width: 100%;
                        padding: 0;
                    }
                    
                    .${styles.header} {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        margin-bottom: 20px;
                        padding-bottom: 15px;
                        border-bottom: 2px solid #333;
                    }
                    
                    .${styles.logo} {
                        max-width: 120px;
                        max-height: 80px;
                        object-fit: contain;
                    }
                    
                    .${styles.companyDetails} {
                        flex: 1;
                        display: flex;
                        justify-content: space-between;
                        margin-left: 20px;
                    }
                    
                    .${styles.storeName} {
                        font-size: 18pt;
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    
                    .${styles.storeAddress} {
                        font-size: 10pt;
                        margin-bottom: 5px;
                    }
                    
                    .${styles.invoiceNumber} {
                        font-size: 16pt;
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    
                    .${styles.customerInfo} {
                        margin: 20px 0;
                    }
                    
                    .${styles.customerInfo} h3 {
                        font-size: 12pt;
                        font-weight: bold;
                        margin-bottom: 8px;
                    }
                    
                    .${styles.lineItems} table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 20px 0;
                    }
                    
                    .${styles.lineItems} th,
                    .${styles.lineItems} td {
                        padding: 8px;
                        text-align: left;
                        border: 1px solid #ddd;
                    }
                    
                    .${styles.lineItems} th {
                        background-color: ${selectedPalette?.tableHeaderColor || '#f0f0f0'};
                        color: ${selectedPalette?.tableHeaderTextColor || '#000000'};
                        font-weight: bold;
                    }
                    
                    .${styles.lineItems} tbody {
                        background-color: ${selectedPalette?.tableBodyBackgroundColor || '#ffffff'};
                        color: ${selectedPalette?.tableBodyTextColor || '#000000'};
                    }
                    
                    .${styles.lineItems} tfoot td {
                        font-weight: bold;
                        font-size: 11pt;
                    }
                    
                    .${styles.paymentCollectionWrapper} {
                        margin: 20px 0;
                        display: flex;
                        justify-content: flex-end;
                    }
                    
                    .${styles.pcwContent} {
                        min-width: 300px;
                    }
                    
                    .${styles.pcwContent} table {
                        width: 100%;
                        margin-bottom: 10px;
                    }
                    
                    .${styles.eachCollection} td {
                        padding: 5px;
                        border-bottom: 1px solid #eee;
                    }
                    
                    .${styles.pcwAmount} {
                        text-align: right;
                        font-weight: bold;
                    }
                    
                    .${styles.paymentStatus} {
                        padding: 10px;
                        margin-top: 10px;
                        display: flex;
                        justify-content: space-between;
                        font-weight: bold;
                        border-radius: 4px;
                    }
                    
                    .${styles.paymentStatus}.${styles.advance} {
                        background-color: #d4edda;
                        color: #155724;
                    }
                    
                    .${styles.paymentStatus}.${styles.neutral} {
                        background-color: #d1ecf1;
                        color: #0c5460;
                    }
                    
                    .${styles.paymentStatus}.${styles.remaining} {
                        background-color: #f8d7da;
                        color: #721c24;
                    }
                    
                    .${styles.footer} {
                        margin-top: 30px;
                        padding-top: 15px;
                        border-top: 1px solid #ddd;
                        text-align: center;
                        font-size: 10pt;
                    }
                    
                    @media print {
                        body {
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="${styles.tallyWrapper}">
                    ${printContent}
                </div>
                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.print();
                            window.onafterprint = function() {
                                window.close();
                            };
                        }, 250);
                    };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
    };

    return (
        <>
            <div
                className={styles.tallyWrapper}
                style={{
                    backgroundColor: selectedPalette?.backgroundColor || "#ffffff",
                    color: selectedPalette?.textColor || "#000000",
                }}
                ref={printRef}
            >
                {/* Header */}
                <header className={styles.header}>
                    {base64Logo ? (
                        <img
                            src={base64Logo}
                            alt="Company Logo"
                            className={styles.logo}
                        />
                    ) : (
                        <div style={{ width: '120px', height: '80px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            Logo
                        </div>
                    )}
                    <div className={styles.companyDetails}>
                        <div className={styles.invoiceStoreMeta}>
                            <div className={styles.storeName}>{activeStore?.storeName || "Company Name"}</div>
                            <div className={styles.storeAddress}>
                                {activeStore?.storeAddress || "123 Business Street"}
                            </div>
                            {activeStore?.gstNumber &&
                                <div className={styles.invoiceDataRow}>
                                    GST Number: {activeStore?.gstNumber}
                                </div>
                            }
                        </div>
                        <div className={styles.invoiceMetaData}>
                            <div className={styles.invoiceNumber}>#{invoiceDataMaster?.invoiceNumber || "12345"}</div>
                            <div>
                                Date: {invoiceDate || "N/A"}<br />
                                Due: {dueDate || "N/A"}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Customer Info */}
                <section className={styles.customerInfo}>
                    <h3>Bill To:</h3>
                    <p>
                        {clientData?.client_name || "Customer Name"}<br />
                        {clientData?.mobile || "Phone Number"}<br />
                    </p>
                </section>

                {/* Line Items */}
                <section className={styles.lineItems}>
                    <table>
                        <thead
                            style={{
                                backgroundColor: selectedPalette?.tableHeaderColor,
                                color: selectedPalette?.tableHeaderTextColor,
                            }}
                        >
                            <tr>
                                <th>#</th>
                                {itemColumns.map(col => <th key={col.key}>{col.label}</th>)}
                            </tr>
                        </thead>
                        <tbody
                            style={{
                                backgroundColor: selectedPalette?.tableBodyBackgroundColor,
                                color: selectedPalette?.tableBodyTextColor,
                            }}
                        >
                            {productData?.length > 0 ? (
                                productData.map((item, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        {itemColumns.map(col => <td key={col.key}>{getColumnValue(item, col.key)}</td>)}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={itemColumns.length + 1} style={{ textAlign: "center" }}>
                                        No items to display.
                                    </td>
                                </tr>
                            )}

                            {activeStore?.gstNumber &&
                                <>
                                    <tr>
                                        <td colSpan={itemColumns.length}>Total Taxable</td>
                                        <td>₹{calculateTaxAndTaxableValue(calculateGrandTotal(), 5)?.taxableValue?.toFixed(2) || "0.00"}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={itemColumns.length}>Total Tax (5%)</td>
                                        <td>₹{calculateTaxAndTaxableValue(calculateGrandTotal(), 5)?.totalTax?.toFixed(2) || "0.00"}</td>
                                    </tr>
                                </>
                            }
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={itemColumns.length}>Grand Total</td>
                                <td>₹{calculateGrandTotal()?.toFixed(2) || "0.00"}</td>
                            </tr>
                        </tfoot>
                    </table>
                </section>

                <section className={styles.paymentCollectionWrapper}>
                    <div className={styles.pcwSpace}></div>
                    <div className={styles.pcwContent}>
                        <table>
                            <tbody>
                                {invoiceDataMaster?.collectionData?.map((itemCollection, indexCollection) => (
                                    <tr key={indexCollection} className={styles.eachCollection}>
                                        <td>
                                            {itemCollection?.mode?.replace("collectionVia", 'Collection Via ')}
                                        </td>
                                        <td className={styles.pcwAmount}>
                                            ₹{itemCollection?.amount}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div
                            className={`${styles.paymentStatus} ${invoiceDataMaster?.remainingAmount < 0
                                ? styles.advance
                                : invoiceDataMaster?.remainingAmount === 0
                                    ? styles.neutral
                                    : styles.remaining
                                }`}
                        >
                            <div>
                                {invoiceDataMaster?.remainingAmount < 0
                                    ? "Advance Payment"
                                    : invoiceDataMaster?.remainingAmount === 0
                                        ? "No Due"
                                        : "Remaining Payment"}
                            </div>
                            <div>
                                ₹{Math.abs(invoiceDataMaster?.remainingAmount).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className={styles.footer}>
                    <p>Thank you for your visit!</p>
                    {customFields?.paymentMethods && (
                        <p>
                            Payment Methods: {customFields?.paymentMethods?.join(", ") || "N/A"}
                        </p>
                    )}
                </footer>
            </div>

            <div className={styles.actions}>
                <button
                    onClick={handleGeneratePDF}
                    disabled={isGeneratingPDF}
                    style={{
                        padding: '12px 24px',
                        marginRight: '10px',
                        backgroundColor: isGeneratingPDF ? '#ccc' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                    }}
                >
                    {isGeneratingPDF ? 'Generating PDF...' : '📥 Download PDF'}
                </button>
                <button
                    onClick={handlePrint}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                    }}
                >
                    🖨️ Print Invoice
                </button>
            </div>
        </>
    );
};

export default TallyTheme;